import Heading2 from "components/Heading/Heading2";
import LoginModal from "components/LoginModal/LoginModal";
import StayCard from "components/StayCard/StayCard";
import SmartFilters from "components/SmartFilters/SmartFilters";
import StableMap from "components/InteractiveMap/StableMap";
import { motion } from "framer-motion";
import { useAppDispatch, useAppSelector, useScrollToTop } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { FaHome } from "react-icons/fa";
import { HiOutlineHome, HiOutlineSparkles } from "react-icons/hi";
import { MapIcon, ListBulletIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchLocationsFiltered, getAllValidateLocations } from "redux/features/locations/locationSlice";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Pagination from "shared/Pagination/Pagination";
import { SkeletonLoader } from "utils/SkeletonLoader";
import { EASING } from "../../../utils/easingUtils";
import "components/InteractiveMap/InteractiveMap.css";

interface Location {
  id: string;
  title?: string;
  address?: string;
  city?: string;
  latitude?: number | string;
  longitude?: number | string;
  price?: number;
  visitor?: number;
  finalLatitude?: number;
  finalLongitude?: number;
  isFallback?: boolean;
}

export interface LocationsGridFilterCardProps {
  className?: string;
  data?: Location[];
  title?: string;
}

const LocationsGridFilterCard: FC<LocationsGridFilterCardProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const locationState = useAppSelector((state) => state.featureLocation?.locations);

  const [filters, setFilters] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const [page, setPage] = useState(initialPage);

  // Get all filter parameters from URL
  const event = searchParams.get("event");
  const region = searchParams.get("region");
  const city = searchParams.get("city");
  const guests = searchParams.get("guests");
  const category = searchParams.get("category");
  const visitor = searchParams.get("visitor");
  const minPrice = searchParams.get("minPrice");
  const maxPrice = searchParams.get("maxPrice");
  const title = searchParams.get("title");

  const pathname = location.pathname;
  const decodedPathname = decodeURIComponent(pathname);
  const afterEvent = decodedPathname.split("/event/")[1];
  useScrollToTop();

  // Build filters object from URL parameters
  const buildFiltersFromURL = () => {
    const urlFilters: any = {};

    if (event) urlFilters.events = event;
    if (region) urlFilters.region = region;
    if (city) urlFilters.city = city;
    if (guests) urlFilters.capacity = guests;
    if (category) urlFilters.category = category;
    if (visitor) urlFilters.visitor = visitor;
    if (minPrice) urlFilters.minPrice = minPrice;
    if (maxPrice) urlFilters.maxPrice = maxPrice;
    if (title) urlFilters.title = title;

    return urlFilters;
  };

  useEffect(() => {
    setIsLoading(true);
    const activePage = parseInt(searchParams.get("page") || "1", 10);
    setPage(activePage);

    const urlFilters = buildFiltersFromURL();
    setFilters(urlFilters);

    if (Object.keys(urlFilters).length > 0 || afterEvent) {
      if (afterEvent) {
        urlFilters.events = afterEvent;
      }
      dispatch(fetchLocationsFiltered({ pageNumber: activePage, filters: urlFilters })).then(() =>
        setIsLoading(false)
      );
    } else {
      dispatch(getAllValidateLocations(activePage)).then(() => setIsLoading(false));
    }
  }, [location.search, dispatch, afterEvent]);

  const handleApplyFilters = (newFilters: any) => {
    setIsLoading(true);
    const updatedFilters = { ...filters, ...newFilters };

    // Preserve event, region, city, and guests from URL if they exist
    if (event) updatedFilters.events = event;
    if (region) updatedFilters.region = region;
    if (city) updatedFilters.city = city;
    if (guests) updatedFilters.capacity = guests;

    setFilters(updatedFilters);

    dispatch(fetchLocationsFiltered({ pageNumber: page, filters: updatedFilters })).then(() =>
      setIsLoading(false)
    );
  };

  // Handle smart filters changes
  const handleSmartFiltersChange = (smartFilters: any) => {
    setIsLoading(true);

    // Check if this is a clear all filters action (empty object)
    const isClearAll = Object.keys(smartFilters).length === 0;

    if (isClearAll) {
      // Clear all filters and show all locations
      setFilters({});

      // Clear URL parameters
      const params = new URLSearchParams();
      params.set("page", "1");
      navigate(`${location.pathname}?${params.toString()}`);

      // Fetch all locations without filters
      dispatch(getAllValidateLocations(1)).then(() => setIsLoading(false));
      return;
    }

    // Transform smart filters to API format
    const apiFilters: any = {};

    if (smartFilters.search) apiFilters.title = smartFilters.search;
    if (smartFilters.location) apiFilters.region = smartFilters.location;
    if (smartFilters.minPrice) apiFilters.minPrice = smartFilters.minPrice;
    if (smartFilters.maxPrice) apiFilters.maxPrice = smartFilters.maxPrice;
    if (smartFilters.capacity) apiFilters.visitor = smartFilters.capacity;
    if (smartFilters.amenities && smartFilters.amenities.length > 0) {
      apiFilters.amenities = smartFilters.amenities.join(",");
      console.log("🎯 Frontend sending amenities to API:", apiFilters.amenities);
    }

    // Handle quick filters
    if (smartFilters.availableWeekend) apiFilters.availableWeekend = true;
    if (smartFilters.nearMetro) apiFilters.nearMetro = true;
    if (smartFilters.instantBook) apiFilters.instantBook = true;
    if (smartFilters.trending) apiFilters.trending = true;
    if (smartFilters.category) apiFilters.category = smartFilters.category;

    // Handle sorting
    if (smartFilters.sortBy) apiFilters.sortBy = smartFilters.sortBy;

    // Preserve existing URL filters
    if (event) apiFilters.events = event;
    if (region && !smartFilters.location) apiFilters.region = region;
    if (city && !smartFilters.location) apiFilters.city = city;
    if (guests && !smartFilters.capacity) apiFilters.capacity = guests;

    setFilters({ ...filters, ...apiFilters });

    // Update URL
    const params = new URLSearchParams(location.search);
    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] && apiFilters[key] !== "") {
        params.set(key, apiFilters[key].toString());
      }
    });
    params.set("page", "1");
    navigate(`${location.pathname}?${params.toString()}`);

    dispatch(fetchLocationsFiltered({ pageNumber: 1, filters: apiFilters })).then(() => setIsLoading(false));
  };

  const handleApplyFiltersRegionEvent = (newFilters: any) => {
    setIsLoading(true);
    const filter = {
      ...filters, // Preserve existing filters
      region: newFilters.region,
      events: newFilters.event?.replace(/\s+/g, "-")?.toLowerCase(),
    };

    // Update the URL with all current filters
    const params = new URLSearchParams(location.search);

    // Handle region and event
    if (filter.region) {
      params.set("region", filter.region);
    } else {
      params.delete("region");
    }
    if (filter.events) {
      params.set("event", filter.events);
    } else {
      params.delete("event");
    }

    // Preserve other filter parameters
    if (category) params.set("category", category);
    if (visitor) params.set("visitor", visitor);
    if (minPrice) params.set("minPrice", minPrice);
    if (maxPrice) params.set("maxPrice", maxPrice);
    if (title) params.set("title", title);
    if (city) params.set("city", city);
    if (guests) params.set("guests", guests);

    params.set("page", page.toString());
    navigate(`${location.pathname}?${params.toString()}`);

    dispatch(fetchLocationsFiltered({ pageNumber: page, filters: filter })).then(() => setIsLoading(false));
  };

  const handlePageChange = (newPage: any) => {
    setPage(newPage);
    const params = new URLSearchParams(location.search);
    params.set("page", newPage.toString());
    navigate(`${location.pathname}?${params.toString()}`);
  };

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [viewMode, setViewMode] = useState<"list" | "map">("list");
  const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [favorites, setFavorites] = useState<string[]>([]);
  const [mapBounds, setMapBounds] = useState<any>(null);

  // Handlers for map synchronization
  const handleLocationSelect = (location: Location) => {
    setSelectedLocationId(location.id);
    // Navigate to location detail page
    // navigate(`/location/${location.id}`);
  };

  const handleLocationHover = (location: Location | null) => {
    setHoveredLocationId(location?.id || null);
  };

  const handleBoundsChange = (bounds: any) => {
    setMapBounds(bounds);
    // Optionally filter locations based on map bounds
  };

  const handleFavoriteToggle = (locationId: string) => {
    setFavorites((prev) => {
      if (prev.includes(locationId)) {
        return prev.filter((id) => id !== locationId);
      } else {
        return [...prev, locationId];
      }
    });
  };

  return (
    <div className={`nc-SectionGridFilterCard ${className}`} data-nc-id="SectionGridFilterCard">
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black opacity-50"></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="flex flex-col items-center p-6 bg-white rounded-lg dark:bg-neutral-900">
              <div className="self-end mb-4">
                <ButtonSecondary onClick={() => setShowLoginModal(false)}>X</ButtonSecondary>
              </div>
              <LoginModal LoginModal={(e) => setShowLoginModal(e)} />
            </div>
          </div>
        </div>
      )}
      <div className="mt-5 mb-8">
        <Heading2
          heading={
            <div className="flex items-center">
              <HiOutlineSparkles className="w-5 h-5 mr-2 text-blue-500 lg:w-5 lg:h-5" />
              <span>Recherche intelligente d'espaces</span>
            </div>
          }
          subHeading="Utilisez nos filtres avancés pour trouver l'espace parfait en quelques clics"
        />
      </div>

      <SmartFilters
        className="mb-8"
        onFiltersChange={handleSmartFiltersChange}
        currentFilters={{
          ...filters,
          location: city || region,
          capacity: guests,
        }}
      />

      {/* Stats et informations */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {locationState?.count ? (
              <span>
                <span className="font-semibold text-blue-600 dark:text-blue-400">{locationState.count}</span>{" "}
                espace{locationState.count > 1 ? "s" : "(s)"} trouvé{locationState.count > 1 ? "s" : "(s)"}
                {Object.keys(filters).length > 0 && " avec vos critères"}
              </span>
            ) : (
              "Recherche en cours..."
            )}
          </div>

          {/* Toggle Vue Liste/Carte */}
          {locationState?.locations && locationState.locations.length > 0 && (
            <div className="flex items-center space-x-4">
              {/* Toggle Buttons */}
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setViewMode("list")}
                  className={`flex items-center px-3 py-2 rounded-md font-medium transition-all duration-200 ${
                    viewMode === "list"
                      ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  }`}
                >
                  <ListBulletIcon className="w-4 h-4 mr-2" />
                  Liste
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setViewMode("map")}
                  className={`flex items-center px-3 py-2 rounded-md font-medium transition-all duration-200 ${
                    viewMode === "map"
                      ? "bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                  }`}
                >
                  <MapIcon className="w-4 h-4 mr-2" />
                  Carte
                </motion.button>
              </div>
            </div>
          )}

          {locationState?.count > 0 && (
            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
              <span>
                Page {page} sur {locationState?.totalPages}
              </span>
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center mt-16">
          <SkeletonLoader />
        </div>
      ) : !locationState?.locations || locationState.count === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASING.easeOut }}
            className="flex flex-col items-center text-center max-w-md"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900 dark:to-purple-900 rounded-full flex items-center justify-center mb-6">
              <FaHome className="text-3xl text-blue-500" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Aucun espace trouvé</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
              Essayez de modifier vos critères de recherche ou explorez d'autres catégories pour découvrir plus
              d'espaces disponibles.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => handleSmartFiltersChange({})}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl font-semibold transition-all duration-300"
            >
              Réinitialiser les filtres
            </motion.button>
          </motion.div>
        </div>
      ) : viewMode === "list" ? (
        <motion.div
          className="grid grid-cols-1 gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {Array.isArray(locationState?.locations) &&
            locationState?.locations.map((stay: any, index: any) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1, ease: EASING.easeOut }}
              >
                <StayCard
                  data={stay}
                  galleryImgs={stay.images}
                  showLoginModal={showLoginModal}
                  setShowLoginModal={setShowLoginModal}
                />
              </motion.div>
            ))}
        </motion.div>
      ) : (
        <div className="space-y-4">
          {/* Info message when filters are active */}
          {(Object.keys(filters).length > 0 || city || region || category || event) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3"
            >
              <div className="flex items-center space-x-2">
                <span className="text-blue-600 dark:text-blue-400">🗺️</span>
                <span className="text-sm text-blue-800 dark:text-blue-200">
                  Carte filtrée • {locationState?.count || 0} espace{(locationState?.count || 0) !== 1 ? "s" : ""}
                  {city && ` • ${city}`}
                  {category && ` • ${category}`}
                  {event && ` • ${event}`}
                </span>
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="w-full h-[calc(100vh-220px)] sm:h-[calc(100vh-200px)] min-h-[600px] sm:min-h-[700px] rounded-xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700"
          >
            <StableMap
              embedded={true}
              locations={(() => {
                return locationState.locations.map((loc: any) => ({
                  id: loc.id,
                  title: loc.title,
                  address: loc.address,
                  city: loc.city,
                  latitude: parseFloat(loc.latitude),
                  longitude: parseFloat(loc.longitude),
                  price: loc.minPrice,
                  visitor: loc.visitor,
                  images: loc.images || [],
                  type: loc.type || loc.CategoryLocation?.name?.toLowerCase() || "other",
                  rating: loc.rating,
                  isPopular: loc.isPopular,
                  isSuperHost: loc.isSuperHost,
                  availabilityStatus: loc.availabilityStatus,
                  description: loc.description || loc.CategoryLocation?.description,
                  category: loc.CategoryLocation?.name,
                  finalLatitude: loc.finalLatitude,
                  finalLongitude: loc.finalLongitude,
                  isFallback: loc.isFallback,
                }));
              })()}
              onLocationSelect={handleLocationSelect}
              onLocationHover={handleLocationHover}
              hoveredLocationId={hoveredLocationId}
              selectedLocationId={selectedLocationId}
              onBoundsChange={handleBoundsChange}
              onFavoriteToggle={handleFavoriteToggle}
              favorites={favorites}
              showClustering={true}
              showPhotos={true}
            />
          </motion.div>
        </div>
      )}
      {/* Pagination améliorée - seulement en mode liste */}
      {viewMode === "list" && locationState?.totalPages > 1 && (
        <div className="flex flex-col items-center justify-center mt-16 space-y-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Affichage de {(page - 1) * 12 + 1} à {Math.min(page * 12, locationState.count)} sur{" "}
            {locationState.count} espaces
          </div>
          <Pagination currentPage={page} totalPages={locationState?.totalPages} onPageChange={handlePageChange} />
        </div>
      )}
    </div>
  );
};

export default LocationsGridFilterCard;
